import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output, ViewChild, Input, Optional
} from "@angular/core";
import {PANEL_DATA} from "../../../../commons/angular/services/panel.service";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {VisitScheduleResourceModel} from "../../../models/visit-schedule-resource.model";
import {VisitPlanJobModel} from "../../../models/visit-plan-job.model";
import {VisitPlanScheduleFacadeService} from "../../services/visit-plan-schedule-facade.service";
import * as _ from "lodash";
import {ResourceWithUtilizationScoring} from "../../../models/Resource-with-utilization-scoring.model";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {
  VisitPlanExternalAssignRequests
} from "../../../models/visit-plan-create-external-resource-holders-payload.model";
import * as moment from "moment-timezone";
import {VisitScheduleAgentImportOptionModel} from "../../../models/visit-schedule-agent-import-option.model";
import {VisitScheduleResourceHolderExt} from "../../../models/visit-schedule-resource-holder-ext.model";
import {VisitPlanFacadeService} from "../../services/visit-plan-facade.service";
import {VisitPlanAccountFacadeService} from "../../services/visit-plan-account-facade.service";
import {finalize} from "rxjs";
import {VisitScheduleModel} from "../../../models/visit-schedule.model";
import {ToastService} from "../../../../commons/angular/services/toast.service";

interface VisitPlanScheduleResourceSelectListModel {
  visitPlan: VisitPlanModel;
  visitScheduleId: string;
  dateRange: { from: number, to: number };
  agentIds: string[];
  programTypeAliasMap: { [key: string]: string };
  selectJobsMode?: boolean;
  jobs?: any[];
  jobIds?: string[];
}

require('./visit-plan-schedule-resource-select-list.scss');
@Component({
  standalone: false,
  selector: 'app-visit-plan-schedule-resource-select-list',
  templateUrl: './visit-plan-schedule-resource-select-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitPlanScheduleResourceSelectListComponent implements OnInit {

  @Input() showFooter: boolean = true;
  @Output() onClose = new EventEmitter<any>();
  @Output() onResourceSelected = new EventEmitter<string>();
  @Output() onExternalResourceSelected = new EventEmitter<VisitPlanExternalAssignRequests>();

  @Input() public selectedResourceId: string;
  @Input() public externalAssignPayload: VisitPlanExternalAssignRequests;

  public isExternalResourcesMode: boolean = false;
  public queryString: string = '';
  public options: ResourceWithUtilizationScoring[];
  public filteredOptions: ResourceWithUtilizationScoring[];
  public ngPlaceholder: string;
  public isLoading: boolean;
  public isSelectJobsMode: boolean = false;
  public assigning: boolean = false;

  @Input() public visitPlan: VisitPlanModel;
  @Input() public visitScheduleId: string;
  @Input() public dateRange: { from: number, to: number };
  @Input() public agentIds: string[];
  @Input() public jobIds: string[];
  @Input() public jobs?: any[];
  @Input() public callIds?: string[];
  @Input() public programTypeAliasMap: { [key: string]: string };

  public selectedJobIds: string[] = [];
  public errorMessage: string = '';

  constructor(@Optional() @Inject(PANEL_DATA) public data: VisitPlanScheduleResourceSelectListModel,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
              @Inject(VisitPlanFacadeService) private visitPlanFacadeService: VisitPlanFacadeService,
              @Inject(VisitPlanAccountFacadeService) private visitPlanAccountFacadeService: VisitPlanAccountFacadeService,
              @Inject(ToastService) public toastService: ToastService,
              @Inject(VisitPlanScheduleFacadeService) private visitPlanScheduleFacadeService: VisitPlanScheduleFacadeService,
  ) {
  }

  ngOnInit() {
    if (this.data) {
      this.visitPlan = this.data.visitPlan;
      this.visitScheduleId = this.visitScheduleId || this.data.visitScheduleId;
      this.dateRange = this.dateRange || this.data.dateRange;
      this.agentIds = this.agentIds || this.data.agentIds;
      this.jobIds = this.data.jobIds;
      this.jobs = this.data.jobs;
      this.isSelectJobsMode = this.data.selectJobsMode;
      this.programTypeAliasMap = this.data.programTypeAliasMap;
    }

    this.options = [];

    this._getOptions();
  }

  onSelectResource(resourceId) {
    this.selectedResourceId = resourceId;
    this.onResourceSelected.emit(resourceId);
  }

  onSelectExternalResource(option: VisitScheduleAgentImportOptionModel) {
    this.externalAssignPayload = {
      agentId: option.id,
      branchIds: this.visitPlan.branchIds,
      skills: option.skills?.length ? [...option.skills] : undefined,
      startTime: moment.tz(this.dateRange.from, this.visitPlan.zoneId).startOf('day').valueOf(),
      endTime: moment.tz(this.dateRange.to, this.visitPlan.zoneId).endOf('day').minute(45).seconds(0).millisecond(0).valueOf()
    };
    this.onExternalResourceSelected.emit(this.externalAssignPayload);
  }

  onJobIdsSelected() {
    this.isSelectJobsMode = false;
  }

  onToggleIsExternalResourcesMode(isExternalResourcesMode) {
    this.isExternalResourcesMode = isExternalResourcesMode;
    this.externalAssignPayload = !!isExternalResourcesMode ?
      {
        startTime: this.dateRange?.from,
        endTime: this.dateRange?.to,
      } : undefined;

    if (!!isExternalResourcesMode) {
      this.selectedResourceId = undefined;
    }
    this.onExternalResourceSelected.emit(this.externalAssignPayload);
  }

  get dayStartTime(): number {
    return this.dateRange?.from ? moment.tz(this.dateRange.from, this.visitPlan.zoneId).startOf('day').valueOf() : 0;
  }

  get dayEndTime(): number {
    return this.dateRange?.from ? moment.tz(this.dateRange.from, this.visitPlan.zoneId).endOf('day').valueOf() : 0;
  }

  onSubmit() {
    if (this.selectedResourceId) {
      this.onClose.emit({selectedJobIds: this.selectedJobIds, selectedResourceId: this.selectedResourceId});

    } else {
      if (!this.validateTimeRange()) {
        this.cdr.detectChanges();
        return;
      }
      this.assigning = true;
      this.visitPlanFacadeService.importExternalAgentsResourceHoldersDesignated(this.data.visitPlan.id, this.data.visitPlan.scheduleId, [this.externalAssignPayload])
        .pipe(finalize(() => {
          this.assigning = false;
          this.cdr.detectChanges();
        }))
        .subscribe({
          next: (resourceHolders: VisitScheduleResourceHolderExt[]) => {
            const resourceHolder = resourceHolders[0];

            this.onClose.emit({selectedJobIds: this.selectedJobIds, selectedResourceId: resourceHolder.id, refreshSchedule: true});
          },
          error: (err) => {
            this.toastService.error('AGENT_CREATE_ERROR');
          }
        });
    }
  }

  onCancel() {
    this.onClose.emit()
  }

  onQueryChange(query) {
    this.queryString = query;
    this._setOptions();
  }

  onSelectJob(id: string) {
    const index = this.selectedJobIds.indexOf(id);

    if (index === -1) {
      this.selectedJobIds.push(id);
    } else {
      this.selectedJobIds.splice(index, 1);
    }

    if (!this.selectedJobIds?.length) {
      this.dateRange = undefined;
    } else {
      const selectedJobs = this.jobs.filter(job => this.selectedJobIds.includes(job.id));
      if (selectedJobs.length > 0) {
        const minNotBefore = Math.min(...selectedJobs.map(job => job.notBefore).filter(time => time !== undefined));
        const maxNotAfter = Math.max(...selectedJobs.map(job => job.notAfter).filter(time => time !== undefined));

        this.dateRange = {
          from: moment(minNotBefore).startOf('day').valueOf(),
          to: moment(maxNotAfter).endOf('day').valueOf()
        };
      }
    }
  }

  onClearSelection() {
    this.selectedJobIds = [];
  }

  onFromTimeChange(startTime) {
    this.externalAssignPayload.startTime = startTime;
    this.validateTimeRange();
    this.onExternalResourceSelected.emit(this.externalAssignPayload);
    this.cdr.detectChanges();
  }

  onToTimeChange(endTime) {
    this.externalAssignPayload.endTime = endTime;
    this.validateTimeRange();
    this.onExternalResourceSelected.emit(this.externalAssignPayload);
    this.cdr.detectChanges();
  }

  private validateTimeRange(): boolean {
    if (!this.isExternalResourcesMode || !this.externalAssignPayload?.agentId) {
      this.errorMessage = '';
      return true;
    }

    const startTime = this.externalAssignPayload.startTime;
    const endTime = this.externalAssignPayload.endTime;

    if (!startTime || !endTime) {
      this.errorMessage = 'Both start and end times are required';
      return false;
    }

    if (endTime <= startTime) {
      this.errorMessage = 'End time must be after start time';
      return false;
    }

    this.errorMessage = '';
    return true;
  }

  private _getOptions() {
    this.isLoading = true;
    let jobIds = this.jobIds || [];

    if (!jobIds?.length) {
      jobIds = undefined;
    }

    this.visitPlanScheduleFacadeService.getAssignOptions(this.visitPlan.id, this.visitScheduleId, jobIds, this.callIds)
      .subscribe({
        next: (response) => {
          this.options = [...response];
          this.isLoading = false;

          this._setOptions();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }

  private _setOptions() {
    this.filteredOptions = this.options.filter(option => {
      if (!this.queryString) return true;

      const search = this.queryString.toLowerCase();

      return JSON.stringify(option)
        .toLowerCase()
        .includes(search);
    });
  }

  // private _getQueryPayload() {
  //   const params = {
  //     limit: this.loadLimit,
  //     from: this.dateRange.from,
  //     to: this.dateRange.to,
  //     agentIds: this.agentIds,
  //     query: this.queryString,
  //     cursor: this.cursor,
  //     scheduleTypes: 'Availability'
  //   };
  //
  //   Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
  //   return params;
  // }

}
